import { ReactLenis, useLenis } from "lenis/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import "./index.css";
import { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import HelloMessage from "./components/HelloMessage";
import i18n from "i18next";
import { initReactI18next, useTranslation, Trans } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import enTranslations from "./assets/locales/en/translation.json";
// import jaTranslations from "./assets/locales/ja/translation.json";
// import LanguageSwitcher from "./components/LanguageSwitcher";
import RoleList from "./components/RoleList";
import Theme /*, { ThemeSwitcher }*/ from "./components/Theme";
import {
  COLSYN,
  CSICK,
  EldoM,
  kimaru,
  SEQ_01,
  SlideIn,
  TextShuffle,
} from "./components/Snippets";
import COLSYN_IMAGE from "./assets/projects/colsyn.png";
import KIMARU_IMAGE from "./assets/hackathons/kimaru.png";
import SEQ_01_IMAGE from "./assets/hackathons/SEQ-01.png";
import Terminal, { TerminalEntry } from "./components/Terminal";
import ExternalLink from "./components/ExternalLink";
import Position from "./components/Position";

gsap.registerPlugin(ScrollTrigger);
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations,
      },
      // ja: {
      //   translation: jaTranslations,
      // },
    },
    fallbackLng: "en",
    detection: {
      order: ["path", "localStorage", "navigator", "htmlTag"],
      lookupFromPathIndex: 0,
      caches: ["localStorage"],
    },
  });

export function App() {
  const { t, i18n } = useTranslation();

  if (typeof document !== "undefined") {
    document.documentElement.lang = i18n.language;
  }

  const mainRef = useRef<HTMLElement>(null);
  const [activeSection, setActiveSection] = useState("home");
  const sectionIds = [
    "home",
    "projects",
    "hackathons",
    "work-history",
    "contact",
  ];

  const lenis = useLenis(ScrollTrigger.update);
  useEffect(() => {
    if (!lenis) return;

    const onScroll = () => {
      let current = "home";
      let minDistance = Infinity;

      const threshold = window.innerHeight * 0.5;

      for (const id of [...sectionIds].reverse()) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();

          const distance = Math.abs(threshold - rect.bottom);

          if (rect.bottom >= threshold && distance < minDistance) {
            minDistance = distance;
            current = id;
          }
        }
      }

      setActiveSection(current);
    };

    lenis.on("scroll", onScroll);
    onScroll();

    return () => {
      lenis.off("scroll", onScroll);
    };
  }, [lenis]);
  useGSAP(
    () => {
      gsap.to(".slide-in-contact", {
        scrollTrigger: {
          trigger: "#contact-title",
        },
        y: 0,
        opacity: 1,
        stagger: 0.2,
        duration: 0.5,
        ease: "power2.out",
      });
    },
    { scope: mainRef },
  );

  return (
    <>
      <Theme />
      <ReactLenis root>
        <main ref={mainRef} className="relative overflow-clip">
          <nav>
            <a
              className={
                activeSection === "home" ? "active primary" : undefined
              }
              href="#"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#"); }}
            >
              Home
            </a>
            <a
              className={
                activeSection === "projects" ? "active secondary" : undefined
              }
              href="#projects"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#projects"); }}
            >
              Projects
            </a>
            <a
              className={
                activeSection === "hackathons" ? "active tertiary" : undefined
              }
              href="#hackathons"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#hackathons"); }}
            >
              Hackathons
            </a>
            <a
              className={
                activeSection === "work-history" ? "active orange" : undefined
              }
              href="#work-history"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#work-history"); }}
            >
              Work History
            </a>
            <a
              className={
                activeSection === "contact" ? "active quaternary" : undefined
              }
              href="#contact"
              onClick={(e) => { e.preventDefault(); lenis?.scrollTo("#contact"); }}
            >
              Contact
            </a>
          </nav>
          <div className="absolute top-0 w-full h-full max-h-300 bg-linear-to-br from-secondary/30 to-30% to-transparent -z-10" />
          <div className="absolute bottom-0 w-full h-full max-h-300 bg-linear-to-tl from-tertiary/30 to-30% to-transparent -z-10" />

          <section
            id="home"
            className="h-screen min-h-fit p-4 lg:p-8 flex items-center justify-center relative"
          >
            <div className="box w-full max-w-5xl flex flex-col gap-2 z-10">
              <h1
                className={`font-bold leading-none tracking-tight ${
                  i18n.language === "ja"
                    ? "text-[2rem] xs:text-[3.5rem] sm:text-[3.5rem] lg:text-[6rem]"
                    : "text-[3rem] xs:text-[4rem] sm:text-[5rem] lg:text-[8rem]"
                }`}
              >
                {HelloMessage()}
              </h1>
              <RoleList />
            </div>
          </section>
          <section id="projects" className="chunk">
            <h2 className="chunk-title">
              <SlideIn trigger="#projects" duration={0.8} from="top">
                <TextShuffle trigger="#projects" text="Projects"></TextShuffle>
              </SlideIn>
              <hr className="mt-4" />
            </h2>
            <section id="csick" className="project">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-radial from-terminal-light/20 to-50% to-transparent -z-10 pointer-events-none" />
              <SlideIn
                trigger="#csick"
                duration={0.8}
                from="bottom"
                className="space-y-2"
              >
                <span className="project-header">
                  <h3 className="project-title">{CSICK}</h3>
                  <h4 className="project-tag">
                    Automated Rust-C++ FFI Generation
                  </h4>
                </span>
                <p>
                  {CSICK} is a tool to generate Rust bindings for use with C++. It
                  parses the C++ header files and generates Rust code and the
                  necessary glue for it to be called from the C++ program. It's
                  designed to be as seamless and automated as possible, aiding
                  with the gradual adoption of Rust in existing C++ codebases.
                </p>
                <p>
                  Built with Rust. This project was presented at ADC Japan 2026, and a video recording will be available at a later date.
                  Try it out at the{" "}
              <ExternalLink href="https://github.com/joelebutler/csick">
                repository
              </ExternalLink>
                  .
                </p>
              </SlideIn>
              <SlideIn trigger="#csick" duration={0.8} from="bottom">
                <section>
                  <Terminal
                    folder="csick"
                    commands={[
                      new TerminalEntry(<>
                        csick --help</>,
                      ),
                      new TerminalEntry(<>
                        Automated scaffolding system for partial Rust implementation in existing C++ codebases.</>,
                        false,
                      ),
                      new TerminalEntry(<>
                        &nbsp;</>,
                        false
                      ),
                      new TerminalEntry(<>
                        Visit <ExternalLink href="https://github.com/joelebutler/csick" bolded={false} underline={false}>https://github.com/joelebutler/csick</ExternalLink> for more information.</>,
                        false
                      ),
                      new TerminalEntry(<>
                        &nbsp;</>,
                        false
                      ),
                      new TerminalEntry(<>
                        <b><u>Usage:</u></b> csick &lt;COMMAND&gt;</>,
                        false
                      ),
                      new TerminalEntry(<>
                        </>,
                        false
                      ),
                      new TerminalEntry(<>
                        <b><u>Commands:</u></b></>,
                        false
                      ),
                      new TerminalEntry(<>
                          init   Initialize a repository with the CSICK essentials for use</>,
                        false
                      ),
                      new TerminalEntry(<>
                          watch  Watch the source directory and run `go` on any non-managed .h, .cpp, or .rs change</>,
                        false
                      ),
                      new TerminalEntry(<>
                          go     Manually run the standard analysis & extraction, and make updates where needed</>,
                        false
                      ),
                      new TerminalEntry(<>
                          look   Manually run analysis, but do not update"</>,
                        false
                      ),
                      new TerminalEntry(<>
                          help   Print this message or the help of the given subcommand(s)</>,
                        false
                      ),
                      new TerminalEntry(<>
                        &nbsp;</>,
                        false
                      ),
                      new TerminalEntry(<>
                        <b><u>Options:</u></b></>,
                        false
                      ),
                      new TerminalEntry(<>
                          -h, --help     Print help</>,
                        false
                      ),
                      new TerminalEntry(<>
                          -V, --version  Print version</>,
                        false
                      ),
                    ]}
                    className="relative z-10 m-auto"
                  />
                </section>
              </SlideIn>
            </section>
            <section id="colsyn" className="project">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-radial from-accent-cyan/20 to-50% to-transparent -z-10 pointer-events-none" />

              <SlideIn
                trigger="#colsyn"
                duration={0.8}
                from="bottom"
                className="space-y-2"
              >
                <span className="project-header">
                  <h3 className="project-title">{COLSYN}</h3>
                  <h4 className="project-tag">
                    Oakland University Senior Capstone
                  </h4>
                </span>
                <p>
                  {COLSYN} is a color-based sampling synthesizer. Integrate
                  memories into your sound. Traverse the pixels of your image
                  and route their colors through a dual oscillator additive
                  synthesizer and into any four of 17 switchable FX units.
                </p>
                <p>
                  {COLSYN} is currently undergoing the process of moving to open-source! Check back here in the future for more updates.
                  {/* Built with C++ and JUCE. Read more on the{" "}
                  <ExternalLink href="https://www.soundsbyjoel.com/products/COLSYN">
                    download page
                  </ExternalLink>
                  . */}
                </p>
              </SlideIn>
              <SlideIn trigger="#colsyn" duration={0.8} from="bottom">
                <img
                  src={COLSYN_IMAGE}
                  alt="Picture of COLSYN color-sampling synthesizer."
                  className="max-w-7xl m-auto w-full"
                />
              </SlideIn>
            </section>
            <section id="EldoM" className="project">
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-radial from-accent-orange/20 to-50% to-transparent -z-2 pointer-events-none" />

              <SlideIn
                trigger="#EldoM"
                duration={0.8}
                from="bottom"
                className="space-y-2"
              >
                <span className="project-header">
                  <h3 className="project-title">{EldoM}</h3>
                  <h4 className="project-tag">Rhythm-based Platformer</h4>
                </span>
                <p>
                  {EldoM} is a rhythm-based platformer where you control a
                  mushroom to journey through space. It features a custom built
                  frame-accurate event triggering system that allows for tight
                  synchronization of gameplay events to the music.
                </p>
                <p>
                  Built in Unity with FMOD. All original character designs and
                  animations designed by me.
                </p>
              </SlideIn>
              <SlideIn trigger="#EldoM" duration={0.8} from="bottom">
                <iframe
                  src="https://www.youtube.com/embed/dgPE71VPQTg?si=fVxXxQzX4Gi2YCjC"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  className="border-0 w-full aspect-video rounded-xl max-w-7xl m-auto"
                ></iframe>
              </SlideIn>
            </section>
            <h3 className="text-lg text-center">
              ... and many more on my{" "}
              <ExternalLink href="https://github.com/joelebutler">
                GitHub
              </ExternalLink>
              .
            </h3>
          </section>
          <section id="hackathons" className="chunk">
            <h2 className="chunk-title">
              <SlideIn trigger="#hackathons" duration={0.8} from="top">
                <TextShuffle
                  trigger="#hackathons"
                  text="Hackathons"
                ></TextShuffle>
                <hr className="mt-4" />
              </SlideIn>
            </h2>
            <section id="kimaru" className="project">
              {/* <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-radial from-accent-orange/20 to-50% to-transparent -z-2 pointer-events-none" /> */}
              <SlideIn
                trigger="#kimaru"
                duration={0.8}
                from="bottom"
                className="space-y-2"
              >
                <span className="project-header">
                  <h3 className="project-title">{kimaru}</h3>
                  <h4 className="project-tag">
                    A site to make hard decisions easier powered by Gemini.
                  </h4>
                </span>
                <p>
                  Built in 24 hours with Typescript, React, and Bun alongside
                  Tailwind CSS. Powered by Gemini API for complex weighting and
                  ranking logic, and a MongoDB database for user collaboration.
                </p>
                <p>
                  Check out the{" "}
                  <ExternalLink href="https://github.com/joelebutler/kimaru">
                    repository
                  </ExternalLink>
                  . Created alongside{" "}
                  <ExternalLink href="https://github.com/zezmo">
                    @zezmo
                  </ExternalLink>
                  ,{" "}
                  <ExternalLink href="https://github.com/lmurray3-sudo">
                    @lmurray3-sudo
                  </ExternalLink>
                  , and{" "}
                  <ExternalLink href="https://github.com/sophiavecchi">
                    @sophiavecchi
                  </ExternalLink>{" "}
                  for GrizzHacks 2026.
                </p>
              </SlideIn>
              <SlideIn trigger="#kimaru" duration={0.8} from="bottom">
                <img
                  src={KIMARU_IMAGE}
                  alt='Picture of hackathon project "kimaru"'
                  className="max-w-7xl m-auto w-full rounded-4xl"
                />
              </SlideIn>
            </section>
            <section id="seq-01" className="project">
              {/* <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] bg-radial from-accent-orange/20 to-50% to-transparent -z-2 pointer-events-none" /> */}
              <SlideIn
                trigger="#seq-01"
                duration={0.8}
                from="bottom"
                className="space-y-2"
              >
                <span className="project-header">
                  <h3 className="project-title">{SEQ_01}</h3>
                  <h4 className="project-tag">
                    Electron-powered Sequencer & Sampler
                  </h4>
                </span>
                <p>
                  Built in 24 hours with React and TailwindCSS on Electron.
                  Loosely inspired by the Roland TR-909 and other sequencers.
                </p>
                <p>
                  Check out the{" "}
                  <ExternalLink href="https://github.com/joelebutler/seq-01">
                    repository
                  </ExternalLink>
                  . Created alongside{" "}
                  <ExternalLink href="https://github.com/skel101">
                    @skel101
                  </ExternalLink>{" "}
                  for GrizzHacks 2025.
                </p>
              </SlideIn>
              <SlideIn trigger="#seq-01" duration={0.8} from="bottom">
                <img
                  src={SEQ_01_IMAGE}
                  alt='Picture of 2025 GrizzHacks project "seq-01"'
                  className="max-w-7xl m-auto w-full rounded-4xl"
                />
              </SlideIn>
            </section>
          </section>
          <section id="work-history" className="chunk pb-0!">
            <h2 className="chunk-title">
              <SlideIn trigger="#work-history" duration={0.8} from="top">
                <TextShuffle
                  trigger="#work-history"
                  text="Work History"
                ></TextShuffle>
                <hr className="my-4" />
              </SlideIn>
            </h2>
            <div className="px-4 md:px-16 py-4 space-y-4">
              <Position
                title="Acoustic Engineer"
                company="HARMAN International"
                timeFrame="July 2026–Present"
              />
              <Position
                title="Acoustic Engineering Intern"
                company="HARMAN International"
                timeFrame="May 2025 - July 2026"
                responsibilities={[
                  "Developed and designed business tools with TypeScript, React, Electron, Vite and MongoDB.",
                  "Collaborated on existing internal acoustic tools utilizing Qt / C++.",
                ]}
              />
              <Position
                title="Audio & Production Assistant"
                company="Oakland University"
                timeFrame="Oct. 2023 - Present"
                responsibilities={[
                  "Live mixing, recording, live video streams, and managing operations for 100+ live events.",
                ]}
              />
              <Position
                title="NCS Student Network Engineer"
                company="Oakland University"
                timeFrame="Sep. 2024 - Apr. 2025"
                responsibilities={[
                  "Troubleshooting, maintenance, and management of phones, switches, and other networking equipment.",
                ]}
              />
              <Position
                title="IT Intern"
                company="Parker Hannifin"
                timeFrame="Summer 2022, Summer 2023"
                responsibilities={[
                  "Built dynamic webpages with SQL, Visual Basic, and jQuery to create data-driven applications.",
                  "Created complex Power BI reports spanning large and dynamic datasets.",
                  "Designed Python scripts for daily task automation.",
                ]}
              />
            </div>
          </section>
          <section
            id="contact"
            className="relative min-h-fit h-[50vh] pt-10 p-20 flex flex-col gap-4 justify-center"
          >
            <h2
              className="slide-in-contact text-4xl lg:text-5xl font-bold"
              id="contact-title"
            >
              {t("contact", "Contact")}
            </h2>
            <p className="slide-in-contact text-xl">
              <ExternalLink
                href="https://www.linkedin.com/in/joelebutler/"
                ariaHide={true}
              >
                <FontAwesomeIcon icon={faLinkedin} />
              </ExternalLink>
              <span className="mr-1">&nbsp;</span>
              <Trans
                i18nKey="connect_on_linkedin"
                defaults="Connect with me on <0>LinkedIn</0>"
                components={[
                  <ExternalLink
                    href="https://www.linkedin.com/in/joelebutler/"
                    ariaHide={false}
                  >
                    LinkedIn
                  </ExternalLink>,
                ]}
              />
            </p>
            <p className="slide-in-contact text-xl">
              <ExternalLink
                href="https://github.com/joelebutler"
                ariaHide={true}
              >
                <FontAwesomeIcon icon={faGithub} />
              </ExternalLink>
              <span className="mr-1">&nbsp;</span>
              <Trans
                i18nKey="follow_on_github"
                defaults="Follow me on <0>GitHub</0>"
                components={[
                  <ExternalLink href="https://github.com/joelebutler">
                    GitHub
                  </ExternalLink>,
                ]}
              />
            </p>
          </section>
          <footer className="h-fit p-4 project-header text-sm opacity-80 *:flex-1">
            <span className="flex-1 text-nowrap">
              &copy; {new Date().getFullYear()} Joel Butler. All rights
              reserved.
            </span>
            {/* <LanguageSwitcher /> */}
            <span className="flex gap-2 justify-end">
              <ExternalLink
                href="https://www.linkedin.com/in/joelebutler/"
                ariaHide={true}
              >
                <FontAwesomeIcon icon={faLinkedin} />
              </ExternalLink>
              <ExternalLink href="https://github.com/joelebutler" ariaHide={true}>
                <FontAwesomeIcon icon={faGithub} />
              </ExternalLink>
            </span>
          </footer>
        </main>
      </ReactLenis>
    </>
  );
}

export default App;
